import { Popover, Tag, Tooltip, Typography } from 'antd'
import { useLayoutEffect, useRef, useState } from 'react'
import { dateFormatter } from 'utils/dateFormatter'
import type { ResolvedApprovalOptionValue } from '../../types'

/**
 * Reveals clipped labels and includes audit details for recorded decisions.
 * Uses the last grid column whether or not the cell has an action column.
 */
function ApprovalOptionTag({
  option,
}: {
  option: ResolvedApprovalOptionValue
}) {
  const tagRef = useRef<HTMLSpanElement>(null)
  const [truncated, setTruncated] = useState(false)
  const { optionLabel, optionTooltip, state } = option
  const measureLabel = () => {
    const tag = tagRef.current
    if (tag) setTruncated(tag.scrollWidth > tag.clientWidth)
  }

  useLayoutEffect(() => {
    measureLabel()
    const tag = tagRef.current
    if (!tag || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measureLabel)
    observer.observe(tag)
    return () => observer.disconnect()
  }, [optionLabel, state?.type])

  let decision: string | undefined
  let color: string | undefined
  if (state?.type === 'approved') {
    decision = 'Approved'
    color = 'success'
  } else if (state?.type === 'rejected') {
    decision = 'Rejected'
    color = 'error'
  } else if (state?.type === 'cleared') {
    decision = 'Cleared'
  }

  const description = [truncated && optionLabel, optionTooltip]
    .filter(Boolean)
    .join(' - ')
  // Only tags whose focus reveals a popover or tooltip are keyboard stops.
  const focusable = !!state || !!description
  const tag = (
    <Tag
      ref={tagRef}
      color={color}
      aria-label={[optionLabel, decision].filter(Boolean).join(': ')}
      tabIndex={focusable ? 0 : undefined}
      onMouseEnter={measureLabel}
      onFocus={measureLabel}
      style={{
        gridColumn: '-2 / -1',
        justifySelf: 'start',
        marginRight: 0,
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        wordBreak: 'break-word',
      }}
    >
      {optionLabel}
    </Tag>
  )

  if (state) {
    return (
      <Popover
        placement="right"
        trigger={['hover', 'focus']}
        content={
          <div style={{ maxWidth: 360, overflowWrap: 'anywhere' }}>
            <div>
              <Typography.Text>
                {`${decision} on ${dateFormatter.shortDatetime(
                  state.date
                )} by ${state.displayName || state.mail}`}
              </Typography.Text>
            </div>
            {description && (
              <div style={{ marginTop: 8 }}>
                <Typography.Text type="secondary">
                  {description}
                </Typography.Text>
              </div>
            )}
          </div>
        }
      >
        {tag}
      </Popover>
    )
  }

  return (
    <Tooltip placement="right" title={description} trigger={['hover', 'focus']}>
      {tag}
    </Tooltip>
  )
}

export default ApprovalOptionTag
